import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api.ts';
import type { me as meFn } from '../../server/routes/auth.ts';

export type Me = ReturnType<typeof meFn>;

interface Session {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<unknown>;
}

const SessionContext = createContext<Session>({ me: null, loading: true, refresh: async () => undefined });

export function SessionProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return (await api.get<{ user: Me }>('/api/auth/me')).user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 3,
    staleTime: 60_000,
  });
  return (
    <SessionContext.Provider value={{ me: query.data ?? null, loading: query.isPending, refresh: query.refetch }}>{children}</SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);

/**
 * Deploy pause (server/domain/maintenance.ts). Polled while it is on, because the deploy restarts
 * the server in the middle of it and the event that ends it can arrive while the stream is down.
 * A failed poll keeps the last answer, so the banner stays up through the restart.
 */
export function useMaintenance(): { since: number } | null {
  const q = useQuery({
    queryKey: ['maintenance'],
    queryFn: async () => (await api.get<{ maintenance: { since: number } | null }>('/api/health')).maintenance,
    staleTime: 30_000,
    retry: false,
    refetchInterval: (query) => (query.state.data ? 5_000 : false),
  });
  return q.data ?? null;
}

export type ConnectionState = 'online' | 'offline' | 'reconnected';

/**
 * Server-Sent Events: the server only says what changed; we invalidate the matching queries
 * and TanStack Query re-fetches the permission-checked data. After a reconnect everything
 * is re-fetched so the UI never shows stale state as current.
 */
export function useLiveUpdates(enabled: boolean): ConnectionState {
  const qc = useQueryClient();
  const [state, setState] = useState<ConnectionState>('online');
  const wasOffline = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    let es: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let closed = false;
    const connect = () => {
      es = new EventSource('/api/events');
      es.addEventListener('hello', () => {
        if (wasOffline.current) {
          wasOffline.current = false;
          void qc.invalidateQueries();
          setState('reconnected');
          setTimeout(() => setState((s) => (s === 'reconnected' ? 'online' : s)), 4000);
        } else {
          setState('online');
        }
      });
      es.onmessage = (msg) => {
        const ev = JSON.parse(msg.data) as { type: string; id?: string; channelId?: string };
        switch (ev.type) {
          case 'tournament':
            void qc.invalidateQueries({ queryKey: ['table', ev.id] });
            void qc.invalidateQueries({ queryKey: ['fairness', ev.id] });
            void qc.invalidateQueries({ queryKey: ['fairness-pending'] });
            void qc.invalidateQueries({ queryKey: ['tournament', ev.id] });
            void qc.invalidateQueries({ queryKey: ['results', ev.id] });
            void qc.invalidateQueries({ queryKey: ['home'] });
            break;
          case 'tournaments':
            void qc.invalidateQueries({ queryKey: ['tournaments'] });
            void qc.invalidateQueries({ queryKey: ['home'] });
            break;
          case 'chat':
            void qc.invalidateQueries({ queryKey: ['messages', ev.channelId] });
            void qc.invalidateQueries({ queryKey: ['channels'] });
            break;
          case 'notifications':
            void qc.invalidateQueries({ queryKey: ['notifications'] });
            void qc.invalidateQueries({ queryKey: ['home'] });
            break;
          case 'debts':
            void qc.invalidateQueries({ queryKey: ['debts'] });
            void qc.invalidateQueries({ queryKey: ['home'] });
            break;
          case 'session_revoked':
            void qc.invalidateQueries({ queryKey: ['me'] });
            break;
          case 'maintenance':
            // Every running table's clock froze or thawed at once: refetch everything.
            void qc.invalidateQueries();
            break;
        }
      };
      es.onerror = () => {
        es?.close();
        if (closed) return;
        wasOffline.current = true;
        setState('offline');
        void qc.invalidateQueries({ queryKey: ['me'] });
        retry = setTimeout(connect, 3000);
      };
    };
    connect();
    const onOnline = () => {
      if (wasOffline.current) {
        es?.close();
        if (retry) clearTimeout(retry);
        connect();
      }
    };
    window.addEventListener('online', onOnline);
    return () => {
      closed = true;
      es?.close();
      if (retry) clearTimeout(retry);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, qc]);
  return state;
}
